import { StarIcon } from "@heroicons/react/outline";
import moment from "moment";
import { Fragment, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArtifactType,
  AttendeeRole,
  AttendeeStatus,
  DashboardPeopleTableUserArtifactsFragmentFragment,
  DashboardPeopleTableUserMeetingFragmentFragment,
} from "types/graphql-schema";
import { BasicUser, TFLocationState } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import getPeopleTableMetadataQuery from "@apps/dashboard-new/graphql/get-people-table-metadata-query";
import GoalProgress from "@apps/goal-progress/goal-progress";
import { GoalProgressView } from "@apps/goal-progress/goal-progress-constants";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import TimeAgoCustom from "@components/time-ago/time-ago";
import Tooltip from "@components/tooltip/tooltip";
import {
  assertEdgesNonNull,
  assertNonNull,
  getUrl,
  getUrlUsingTypename,
  removeCurrentYear,
  toWithBackground,
} from "@helpers/helpers";

const peopleTableGoalMax = 3;

const HomePeopleRow = ({
  selectedUser,
  user,
  userMetadata,
  loadingArtifacts,
  hideOneOnOneCTA = false,
}: {
  userMetadata?: DashboardPeopleTableUserArtifactsFragmentFragment & {
    meetings: DashboardPeopleTableUserMeetingFragmentFragment[];
  };
  selectedUser: BasicUser & { __typename: "UserNode" };
  user: BasicUser & { __typename: "UserNode" };
  loadingArtifacts: boolean;
  hideOneOnOneCTA?: boolean;
}) => {
  const location = useLocation<TFLocationState>();
  const label = useLabel();
  const currentOrganization = currentOrganizationVar();
  const [
    isShowingCreateRecognitionDialog,
    setIsShowingCreateRecognitionDialog,
  ] = useState(false);
  const [isShowingCreateOneononeDialog, setIsShowingCreateOneononeDialog] =
    useState(false);

  const goals = userMetadata?.goals
    ? assertEdgesNonNull(userMetadata.goals)
    : [];
  const goalCount = userMetadata?.goals?.totalCount || goals.length;
  const maxGoalCountDisplayed =
    goalCount > peopleTableGoalMax ? peopleTableGoalMax - 1 : goalCount;
  const lastRecognition = userMetadata?.mostRecentlyReceivedRecognition;
  const nextMeeting = userMetadata?.meetings?.[0];

  const handleClickGiveRecognition = () => {
    setIsShowingCreateRecognitionDialog(true);
  };
  const handleCloseGiveRecognition = () => {
    setIsShowingCreateRecognitionDialog(false);
  };
  const handleClickScheduleMeeting = () => {
    setIsShowingCreateOneononeDialog(true);
  };
  const handleCloseScheduleMeeting = () => {
    setIsShowingCreateOneononeDialog(false);
  };

  const userTo = getUrlUsingTypename(user, location);

  // RENDER
  return (
    <Fragment>
      {isShowingCreateRecognitionDialog && (
        <ArtifactCreationDialog
          formOptions={{
            artifactType: ArtifactType.Recognition,
            recognitionRecipients: [user],
          }}
          onClose={handleCloseGiveRecognition}
          refetchQueries={[getPeopleTableMetadataQuery]}
        />
      )}
      {isShowingCreateOneononeDialog && (
        <MeetingDialog
          formOptions={{
            title: `${label("oneonone")}: ${user.firstName} <> ${
              selectedUser.firstName
            }`,
            isFormalOneonone: true,
            attendees: [
              {
                ...selectedUser,
                email: assertNonNull(selectedUser.email),
                role: AttendeeRole.Required,
                participantStatus: AttendeeStatus.NotResponded,
              },
              {
                ...user,
                email: assertNonNull(user.email),
                role: AttendeeRole.Required,
                participantStatus: AttendeeStatus.NotResponded,
              },
            ],
            facilitatorId: selectedUser.id,
          }}
          onClose={handleCloseScheduleMeeting}
          refetchQueries={[getPeopleTableMetadataQuery]}
        />
      )}
      <div className="flex gap-8 px-2 py-3">
        <div className="text-gray-700 w-[200px] shrink-0">
          <div className="flex items-center gap-2">
            <AppLink to={userTo}>
              <Avatar user={user} size="5" />
            </AppLink>
            <AppLink
              to={userTo}
              className="text-gray-900 text-sm hover:underline truncate"
            >
              {user.name}
            </AppLink>
            {userMetadata?.primaryManager?.id === user.id && (
              <Tooltip text="Primary Manager">
                <span>
                  <StarIcon className="h-3 w-3 text-gray-500" />
                </span>
              </Tooltip>
            )}
          </div>
          {currentOrganization.featureFlags.goals && (
            <div className="mt-1 ml-7 min-h-5 flex items-center">
              {loadingArtifacts ? (
                <div className="skeleton h-5 w-full rounded" />
              ) : (
                <div className="flex flex-none items-center gap-0.5 ">
                  {goals.length === 0 && (
                    <span className="text-gray-400">
                      No {label("goal", { pluralize: 0 })}
                    </span>
                  )}
                  {goals
                    .slice(0, maxGoalCountDisplayed)
                    .map(
                      (goal) =>
                        goal.__typename === "GoalArtifactNode" && (
                          <GoalProgress
                            key={goal.id}
                            artifact={goal}
                            withPopover={true}
                            goalProgressView={GoalProgressView.pillOnly}
                          />
                        ),
                    )}
                  {goalCount > maxGoalCountDisplayed && (
                    <AppLink
                      to={`/goals/user/${user.id}`}
                      className="text-xs ml-1 hover:underline"
                    >
                      +{goalCount - maxGoalCountDisplayed}
                    </AppLink>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {currentOrganization.featureFlags.meetings && (
          <div className="w-36">
            <div className="text-sm text-gray-500 mb-1">
              Next{" "}
              {hideOneOnOneCTA
                ? "Meeting"
                : label("1-on-1", { capitalize: true })}
            </div>
            <div>
              {loadingArtifacts ? (
                <div className="skeleton h-5 w-full rounded" />
              ) : nextMeeting ? (
                <AppLink
                  to={getUrl({
                    meetingGroupId: nextMeeting.meetingGroupId,
                    meetingId: nextMeeting.id,
                  })}
                  className="text-blue-link hover:underline"
                >
                  {removeCurrentYear(
                    moment(nextMeeting.startDatetime)
                      .format("MMM D, h:mma")
                      .replace(":00", ""),
                  )}
                </AppLink>
              ) : hideOneOnOneCTA ? null : (
                <button
                  className="text-blue-link hover:underline text-left"
                  onClick={handleClickScheduleMeeting}
                >
                  Schedule {label("oneonone")}
                </button>
              )}
            </div>
          </div>
        )}

        {currentOrganization.featureFlags.recognitions && (
          <div className="w-36">
            <div className="text-sm text-gray-500 mb-1">
              Last {label("recognition", { capitalize: true })}
            </div>
            {loadingArtifacts ? (
              <div className="skeleton h-5 w-full rounded" />
            ) : (
              <div className="tracking-tight ">
                {lastRecognition &&
                lastRecognition.__typename === "RecognitionArtifactNode" ? (
                  <AppLink
                    to={toWithBackground({
                      pathname: getUrl({
                        artifactId: lastRecognition.id,
                        artifactType: lastRecognition.artifactType,
                      }),
                      location,
                    })}
                    className="hover:underline text-gray-800"
                  >
                    <TimeAgoCustom
                      date={moment(lastRecognition.created).toDate()}
                    />
                  </AppLink>
                ) : (
                  <button
                    className="hover:underline text-gray-800"
                    onClick={handleClickGiveRecognition}
                  >
                    Give a {label("recognition")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex items-center justify-end flex-1">
          <Button
            theme={buttonTheme.lightBlue}
            size={ButtonSize.medium}
            text="View Profile"
            to={userTo}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default HomePeopleRow;
